/**
 * useQuery hook for the story folder tree.
 *
 * Replaces `storyListEffects.getStoryFolders` (compose-app). Mirrors the same
 * `POST /api/stories/v1/rootFolder/:entityId` call via `ensureRootFolder`,
 * which returns the root folder and its subtree. The `displayPublicationTab`
 * flag governs whether the publications folder is included (always `true` in
 * current flagging, kept as a param for forward-compatibility).
 *
 * Auth note (§1): Story List is non-admin reachable. `ensureRootFolder` uses
 * cookie/session auth via `transport` — safe.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';

import { ensureRootFolder } from '../../../adapter/stories-service/api';
import { useToast } from '../../Toast/context/useToast.context';
import { IStoryFolder } from '../../../../types/storyListTypes';
import { storyListQueryKeys, StoryFoldersKeyParams } from './storyListQueryKeys';

export type UseStoryFoldersParams = StoryFoldersKeyParams | null;

export type StoryFoldersResult = {
  storyFolders: IStoryFolder[];
};

export function useStoryFolders(params: UseStoryFoldersParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<StoryFoldersResult, Error>({
    queryKey: params
      ? storyListQueryKeys.storyFolders(params)
      : ([...storyListQueryKeys.all, 'storyFolders', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { entityId, displayPublicationTab } = params;
      const response = await ensureRootFolder(entityId, displayPublicationTab);
      return { storyFolders: response.storyFolders as unknown as IStoryFolder[] };
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
